// Absolute imports
import React, { type Node } from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { get, result } from 'lodash';
import { Button, Col, Image, Row } from 'angieslist-react-components';
import { analytics, Track, urlCatalog } from 'angieslist-app-platform';
import { CameraIcon24, InfoIcon24 } from '@angi/icons/design-system-icons/ui-icons/24';

// Relative imports
import { defaultPageProps } from '../../../../../app/browser/analyticsDefaultProperties';
import './coverPhotoUploader.scss';
import CameraIcon from '../../../../profile/browser/assets/camera.svg';
import ErrorIcon from '../../assets/error-icon.svg';
import LoaderSpinner from '../../assets/loader-spinner.svg';
import type { ProfileReducerState } from '../../../../profile/common/profileReducer';
import profileActions from '../../../../profile/common/profileActions';
import imageActions from '../../../../../app/common/images/imagesActions';
import { itemClicked } from '../../../../../app/browser/analyticsHelpers';
import { ImagesReducerState } from '../../../../store/common/imagesReducer';

type Props = {
  serviceProviderProfile: ProfileReducerState,
  images: ImagesReducerState,
  createCoverPhoto: typeof imageActions.createCoverPhoto,
  resetCoverPhotoRenderError: () => void,
  setCoverPhotoRenderError: () => void,
  coverPhotoRenderError: Boolean,
  sizeError: Boolean,
  capitalizedStep: string,
  businessName: string,
  uuid: string,
  cameraButtonDescription: string,
  isMiniprofile: boolean,
  serviceProviderId: string,
  profileUrl: string,
};

const acceptableImageFiles = 'image/png, image/jpg, image/gif, image/jpeg';

export class CoverPhotoUploaderComponent extends React.Component<Props> {
  props: Props;
  coverPhotoRef: any;

  coverPhotoValidation = async (file) => {
    const {
      createCoverPhoto,
      serviceProviderProfile,
      resetCoverPhotoRenderError,
      setCoverPhotoRenderError,
      uuid,
    } = this.props;
    // 10MB max size limit
    const maxSizeMB = 10485760;
    const serviceProviderId = serviceProviderProfile.currentProfileUAPI.response.legacyId;
    if (file ? file.size < maxSizeMB : false) {
      await createCoverPhoto(
        uuid,
        file,
        serviceProviderId,
      );

      resetCoverPhotoRenderError();
    } else {
      setCoverPhotoRenderError(true);
    }
  }

  handleUploadClick = () => result(this.coverPhotoRef, 'click');

  render(): Node {
    const {
      coverPhotoRenderError,
      serviceProviderProfile,
      sizeError,
      images,
      capitalizedStep,
      businessName,
      cameraButtonDescription,
      isMiniprofile,
      serviceProviderId,
      profileUrl,
    } = this.props;

    const coverPhotoUrl = get(images, 'createCoverPhoto.response.body.images[0].getUrl', false)
      || get(serviceProviderProfile, 'currentProfileUAPI.response.coverPhotoUrl', false);

    const isCoverPhotoLoading = get(images, 'createCoverPhoto.isLoading', false);

    return (
      <div id="cover-photo-uploader-container">
        <div style={{ align: 'center' }}>
          <If condition={isCoverPhotoLoading}>
            <div id="cover-photo-uploader--loading-backdrop" />
            <Image src={LoaderSpinner} className="loader-spinner-icon icon-spin" />
          </If>
          <input
            id="cover-photo-upload-input"
            type="file"
            accept={acceptableImageFiles}
            onChange={e => this.coverPhotoValidation(e.target.files[0])}
            ref={(ref) => {
              this.coverPhotoRef = ref;
            }}
            className="hidden"
          />
          <Choose>
            <When condition={coverPhotoUrl && !coverPhotoRenderError}>
              <Track>
                <div
                  id="cover-photo--image-container"
                  onClick={this.handleUploadClick}
                  onKeyDown={this.handleUploadClick}
                  role="button"
                  tabIndex={0}
                  data-track-step={capitalizedStep}
                >
                  <Image
                    id="cover-photo--image"
                    src={coverPhotoUrl}
                    onError={this.setCoverPhotoRenderError}
                    alt="30x30"
                  />
                  <div id="edit-photo-pill">
                    <CameraIcon24 id="edit-photo-pill-camera-icon" />
                    <span id="edit-photo-pill-text" className="National2-Medium">Edit</span>
                  </div>
                </div>
              </Track>
            </When>
            <Otherwise>
              <Track>
                <div
                  id="cover-photo--image-placeholder"
                  data-track-step={`Profile Builder : ${capitalizedStep}`}
                  className="text-row-center"
                  onClick={this.handleUploadClick}
                  onKeyDown={this.handleUploadClick}
                  role="button"
                  tabIndex={0}
                >
                  <div id="icon-and-text-container">
                    <div id="cover-photo--camera-icon">
                      <img src={CameraIcon} alt="Camera Icon" />
                    </div>
                    <h5 className="National2-Medium">{cameraButtonDescription}</h5>
                  </div>
                </div>
              </Track>
            </Otherwise>
          </Choose>
        </div>
        <div style={{ align: 'center' }} className={isMiniprofile ? 'mini-profile-border' : ''}>
          <Choose>
            <When condition={!isMiniprofile}>
              <div id="cover-photo--business-placeholder">
                <div id="cover-photo--business-name-container">
                  <h4 id="cover-photo--business-name" className="National2-Bold">{businessName}</h4>
                  <div id="profile-info-placeholder-long" className="profile-info-placeholder" />
                  <div id="profile-info-placeholder-short" className="profile-info-placeholder" />
                </div>
              </div>
            </When>
            <Otherwise>

              <Row className="cover-photo--miniprofile-placeholder">
                <h4 id="Edit-Profile--Business-Name" className="National2-Medium" >{businessName}</h4>
              </Row>
              <If condition={!coverPhotoUrl}>
                <Row>
                  <Col xs={12} lg={12} md={12} sm={12} className="Edit-profile-Tip-Container">
                    <div className="Edit-Profile-Tip-Text-Container">
                      <InfoIcon24 className="tip-icon-mini-profile" />
                      <span>
                        Tip: Use a horizontal image of you or your team.
                      </span>
                    </div>
                  </Col>
                </Row>
              </If>
              <Row>
                <Track>
                  <Button
                    id="Edit-Profile--View-My-Profile"
                    bsStyle="primary-office"
                    className="nav-submit-button National2-Bold btn-flat btn-primary-office btn btn-primary-office btn-block"
                    target="_blank"
                    href={profileUrl
                        || urlCatalog.member.serviceProvider(serviceProviderId)}
                    flat
                    block
                  >
                      Preview Profile
                  </Button>
                </Track>
              </Row>
            </Otherwise>
          </Choose>
          <If condition={coverPhotoRenderError || sizeError}>
            <div className="error-text-row">
              <img id="cover-photo--error-icon" src={ErrorIcon} alt="Error Icon" />
              <Choose>
                <When condition={sizeError}>
                  <div className="error-text National2-Regular">
                    File cannot be larger than 10MB. Accepted file types: JPG/JPEG, PNG, GIF
                  </div>
                </When>
                <When condition={coverPhotoRenderError}>
                  <div className="error-text National2-Regular">
                      Upload your profile photo
                  </div>
                </When>
              </Choose>
            </div>
          </If>
        </div>
      </div>);
  }
}

const analyticsRuleset = {
  elements: {
    'cover-photo--image-container': itemClicked('<<attributes.data-track-step.value>>', 'Edit photo'),
    'cover-photo--image-placeholder': itemClicked('<<attributes.data-track-step.value>>', 'Choose a photo'),
    'Edit-Profile--View-My-Profile': itemClicked('<<attributes.data-track-step.value>>', 'View My Profile', defaultPageProps),
  },
};

export default (compose(
  analytics({
    ruleset: analyticsRuleset,
  }),
  connect(state => ({
    serviceProviderProfile: state.serviceProviderProfile,
    images: state.images,
  }), {
    ...profileActions,
    ...imageActions,
  }),
)(CoverPhotoUploaderComponent): any);



// WEBPACK FOOTER //
// ./src/modules/setup/browser/components/step2/coverPhotoUploader.js